import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = { cliente: "Cliente", partner: "Partner", prospect: "Prospect" };

export default function MergeDoppioneDialog({ source, onOpenChange, onDone }) {
  const open = !!source;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!source) { setData(null); return; }
    setLoading(true);
    api.get("/anagrafiche/merge-candidates", { params: { kind: source.kind, id: source.id } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Errore nel caricamento dei gemelli"))
      .finally(() => setLoading(false));
  }, [source]);

  const doMerge = async (cand) => {
    const keep = cand.keep, remove = cand.remove;
    if (!window.confirm(
      `Confermi l'unione?\n\n✓ MANTENGO: ${keep.name} (${KIND_LABEL[keep.kind] || keep.kind})\n✕ RIMUOVO: ${remove.name} (${KIND_LABEL[remove.kind] || remove.kind})\n\nAttività, preventivi e note vengono spostati sul record mantenuto. Viene creato un backup automatico.`
    )) return;
    setBusyId(cand.id);
    try {
      const r = await api.post("/anagrafiche/merge", {
        source_kind: source.kind, source_id: source.id,
        target_kind: cand.kind, target_id: cand.id,
      });
      toast.success(`Unite: mantenuto "${r.data.survivor?.name}", rimosso "${r.data.removed?.name}" · ${r.data.activities_moved || 0} attività spostate`);
      onOpenChange?.(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unione non riuscita");
    } finally {
      setBusyId(null);
    }
  };

  const src = data?.source;
  const cands = data?.candidates || [];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onOpenChange?.(false); }}>
      <DialogContent className="max-w-2xl" data-testid="merge-doppione-dialog">
        <DialogHeader>
          <DialogTitle>Unisci anagrafica doppia</DialogTitle>
          <DialogDescription>
            Il sistema propone i record con lo <b>stesso nome</b> di tua competenza. Scegli il gemello da unire:
            il record da <b>mantenere</b> è deciso automaticamente (con P.IVA · cliente · assegnato · più attività).
            P.IVA diverse ⇒ unione bloccata.
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /></div>}

        {!loading && src && (
          <div className="rounded-md border border-border bg-muted/30 p-3 text-xs" data-testid="merge-source">
            <div className="text-[11px] uppercase text-muted-foreground">Anagrafica selezionata</div>
            <div className="font-semibold">{src.name}</div>
            <div className="mt-0.5 text-muted-foreground">
              {(KIND_LABEL[source.kind] || source.kind)} · {src.piva ? `P.IVA ${src.piva}` : "senza P.IVA"} · {[src.city, src.province].filter(Boolean).join(" ") || "—"} · {src.agency} · {src.activities} att. · {src.quotes} prev.
            </div>
          </div>
        )}

        {!loading && cands.length === 0 && (
          <div className="py-6 text-center text-sm text-muted-foreground" data-testid="merge-no-candidates">
            Nessun gemello trovato nella tua competenza.
          </div>
        )}

        {!loading && cands.length > 0 && (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto" data-testid="merge-candidates-list">
            {cands.map((c) => (
              <div key={`${c.kind}-${c.id}`} className="rounded-md border border-border p-3" data-testid={`merge-candidate-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 text-xs">
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className="text-[10px]">{KIND_LABEL[c.kind] || c.kind}</Badge>
                      <span className="font-semibold">{c.name}</span>
                    </div>
                    <div className="mt-0.5 text-muted-foreground">
                      {c.piva ? `P.IVA ${c.piva}` : "senza P.IVA"} · {[c.city, c.province].filter(Boolean).join(" ") || "—"} · {c.agency} · {c.activities} att. · {c.quotes} prev.
                    </div>
                    <div className="mt-1.5 flex items-center gap-1 text-[11px] text-emerald-600">
                      <ShieldCheck className="h-3.5 w-3.5" /> Mantengo <b>{c.keep.name}</b> <ArrowRight className="h-3 w-3" /> rimuovo <b className="text-red-500">{c.remove.name}</b>
                    </div>
                  </div>
                  <Button size="sm" onClick={() => doMerge(c)} disabled={busyId === c.id} data-testid={`merge-do-${c.id}`}>
                    {busyId === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unisci"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
